import { ref, computed, onMounted, onUnmounted } from 'vue';
import api from '@/lib/axios';

const notifications = ref([]);
const isLoading = ref(false);
const error = ref(null);
let pollingInterval = null;
const POLLING_INTERVAL_MS = 30000; // 30 seconds

export function useNotifications() {
    const unreadCount = computed(() =>
        notifications.value.filter(n => !n.read_at).length
    );

    const unreadNotifications = computed(() =>
        notifications.value.filter(n => !n.read_at)
    );

    const fetchNotifications = async () => {
        try {
            isLoading.value = true;
            error.value = null;
            const { data } = await api.get('/api/v1/notifications', {
                params: { include_read: true }
            });
            notifications.value = data.data?.notifications || [];
        } catch (err) {
            console.error('Error fetching notifications:', err);
            error.value = err;
        } finally {
            isLoading.value = false;
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await api.post(`/api/v1/notifications/${notificationId}/read`);
            const notification = notifications.value.find(n => n.id === notificationId);
            if (notification) {
                notification.read_at = new Date().toISOString();
            }
        } catch (err) {
            console.error('Error marking notification as read:', err);
            throw err;
        }
    };

    const markAllAsRead = async () => {
        const unread = notifications.value.filter(n => !n.read_at);
        await Promise.all(unread.map(n => markAsRead(n.id)));
    };

    const startPolling = () => {
        if (pollingInterval) return;
        pollingInterval = setInterval(fetchNotifications, POLLING_INTERVAL_MS);
    };

    const stopPolling = () => {
        if (pollingInterval) {
            clearInterval(pollingInterval);
            pollingInterval = null;
        }
    };

    onMounted(() => {
        fetchNotifications();
        startPolling();
    });

    onUnmounted(() => {
        stopPolling();
    });

    return {
        notifications,
        unreadNotifications,
        unreadCount,
        isLoading,
        error,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        startPolling,
        stopPolling
    };
}
